import { Company } from '@components/company/Company'
import { Employee } from '@components/employee/Employee'
import { Job } from '@components/job/Job'
import { Employee as EmployeeModel } from '@models/Employee'
import {
  getEmployees,
  selectEmployees,
  setEmployeeQuery,
} from '@store/EmployeeReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import {
  SBTable,
  SBTableFilters,
  TableParams,
} from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { useAppDispatch, useAppSelector } from '@utils/hooks'
import { extractSortDataFromSorterResult } from '@utils/table/sorter'
import { Flex, TableColumnsType, TableProps } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'

const EmployeesPage = () => {
  const dispatch = useAppDispatch()

  const items = useAppSelector(selectEmployees())
  const { query, isLoading } = useAppSelector((state) => state.employee)
  const { isOpen } = useSBRM()

  const columns: TableColumnsType<EmployeeModel> = [
    {
      key: 'name',
      title: 'Nom',
      width: 200,
      render: (_, record) => <Employee.Avatar model={record} />,
    },
    {
      key: 'companies',
      title: 'Société',
      render: (_, record) => <Company.Tag id={record.company} />,
    },
    {
      key: 'positions',
      title: 'Poste',
      render: (_, record) => <Job.Tag id={record.job} />,
    },
    {
      key: 'manager',
      title: 'Manager',
      render: (_, record) => (
        <Employee.Avatar id={record.manager} type="condensed" />
      ),
    },
    {
      key: 'hire-date',
      title: "Date d'embauche",
      render: (_, record) =>
        record.hireDate ? dayjs(record.hireDate).format('DD/MM/YYYY') : '--',
    },
    {
      key: 'seniority-date',
      title: "Date d'ancienneté",
      render: (_, record) =>
        record.seniorityDate
          ? dayjs(record.seniorityDate).format('DD/MM/YYYY')
          : '--',
    },
    {
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <SBRM.Actions
          actions={[SBRMAction.Update, SBRMAction.Delete]}
          entity={Entity.employee}
          entityId={record.id}
        />
      ),
    },
  ]

  const handleTableChange: TableProps<EmployeeModel>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    const newQuery: TableParams = {
      pagination,
      filters,
      ...extractSortDataFromSorterResult(sorter),
    }
    dispatch(setEmployeeQuery(newQuery))
    dispatch(getEmployees(newQuery))
  }

  useEffect(() => {
    dispatch(getEmployees(query))
  }, [dispatch, isOpen])

  return (
    <Flex vertical className="container-row">
      <PageTitle
        title="Employés"
        level={5}
        toolbar={
          <SBRM.Actions
            entity={Entity.employee}
            actions={[SBRMAction.Create]}
          />
        }
      />
      <SBTableFilters
        onValuesChange={(filters, search) => {
          const newQuery: TableParams = {
            ...query,
            pagination: { ...query.pagination, current: 1 },
            filters,
            search,
          }
          dispatch(setEmployeeQuery(newQuery))
          dispatch(getEmployees(newQuery))
        }}
        left={[
          {
            name: 'companies',
            type: 'filter',
            label: 'Sociétés',
            style: { maxWidth: 250 },
            input: (
              <SBAsyncSelect
                multiple
                allowClear
                entity={Entity.company}
                style={{ minWidth: '16ch' }}
                placeholder="Sélectionner une société"
              />
            ),
          },
          {
            name: 'positions',
            type: 'filter',
            label: 'Postes',
            input: (
              <SBAsyncSelect
                multiple
                allowClear
                entity={Entity.job}
                style={{ minWidth: '16ch' }}
                placeholder="Sélectionner un poste"
              />
            ),
          },
        ]}
      />
      <SBTable
        scroll={{ x: 600 }}
        columns={columns}
        dataSource={items}
        pagination={query.pagination}
        loading={isLoading}
        onChange={handleTableChange}
      />
    </Flex>
  )
}

export default EmployeesPage
